/**
 * ShiftsPage - Pagină dedicată pentru programarea gărzilor medicilor
 * Refactorizată pentru a conține DOAR gărzi medici (fără tehnicieni sau pacienți)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Tooltip,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Repeat as RepeatIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ro } from 'date-fns/locale';

import apiClient from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { PageHeader } from '../components/ui';
import ShiftsFiltersBar, {
  DatePreset,
  DateRangeValue,
  DoctorOption,
  StatusOption,
} from '../components/shifts/ShiftsFiltersBar';
import { formatDateTime } from '../utils/timezone';

interface RecurrencePattern {
  start_date: string;
  end_date: string;
  days_of_week: number[];
  time_start: string;
  time_end: string;
}

interface Shift {
  id: string;
  tenant_id: string;
  institution_id: string;
  doctor_id: string;
  start_at: string;
  end_at: string;
  modalities: string[];
  status: 'planificata' | 'in_curs' | 'finalizata';
  auto_assign: boolean;
  notes?: string;
  created_at: string;
  is_recurring?: boolean;
  recurrence_pattern?: RecurrencePattern;
  institution_name?: string;
  doctor_name?: string;
}

interface Institution {
  id: string;
  name: string;
}

interface User {
  id: string;
  email: string;
  doctor_profile?: {
    first_name: string;
    last_name: string;
  };
}

interface ShiftCreate {
  institution_id: string;
  doctor_id: string;
  start_at: string;
  end_at: string;
  modalities: string[];
  status?: 'planificata' | 'in_curs' | 'finalizata';
  auto_assign: boolean;
  notes?: string;
}

const ShiftsPage: React.FC = () => {
  const { user } = useAuth();
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [medics, setMedics] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [activeTab, setActiveTab] = useState<'table' | 'calendar'>('calendar');
  const [editingShift, setEditingShift] = useState<Shift | null>(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalShifts, setTotalShifts] = useState(0);

  // Calendar navigation state
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());

  const getMonthRange = (offset: number): DateRangeValue => {
    const base = new Date();
    const month = base.getMonth() + offset;
    const start = new Date(base.getFullYear(), month, 1, 0, 0, 0, 0);
    const end = new Date(base.getFullYear(), month + 1, 0, 23, 59, 59, 999);
    return { start, end };
  };

  // Filters state
  const [selectedInstitution, setSelectedInstitution] = useState<string>('all');
  const [selectedDoctor, setSelectedDoctor] = useState<User | null>(null);
  const [doctorInputValue, setDoctorInputValue] = useState('');
  const [selectedModalities, setSelectedModalities] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<DateRangeValue>(() => getMonthRange(0));
  const [datePreset, setDatePreset] = useState<DatePreset | null>('current_month');

  // Sort state
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // State pentru gărzi expandate în calendar
  const [expandedShifts, setExpandedShifts] = useState<Shift[]>([]);

  const [formData, setFormData] = useState<ShiftCreate>({
    institution_id: '',
    doctor_id: '',
    start_at: '',
    end_at: '',
    modalities: [],
    auto_assign: true,
    notes: ''
  });

  // State pentru programare recursivă
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringStartDate, setRecurringStartDate] = useState<Date | null>(null);
  const [recurringEndDate, setRecurringEndDate] = useState<Date | null>(null);
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [shiftStartTime, setShiftStartTime] = useState<Date | null>(null);
  const [shiftEndTime, setShiftEndTime] = useState<Date | null>(null);

  // Pentru single shift (non-recurring)
  const [singleStartDateTime, setSingleStartDateTime] = useState<Date | null>(null);
  const [singleEndDateTime, setSingleEndDateTime] = useState<Date | null>(null);

  const availableModalities = ['CR', 'CT', 'DR', 'DS', 'DX', 'ECG', 'ES', 'IO', 'MG', 'MR', 'NM', 'OCT', 'OT', 'PT', 'PX', 'RF', 'SR', 'US', 'XA', 'XC', 'XR'];

  const daysOfWeek = [
    { label: 'L', value: 1, name: 'Luni' },
    { label: 'M', value: 2, name: 'Marți' },
    { label: 'M', value: 3, name: 'Miercuri' },
    { label: 'J', value: 4, name: 'Joi' },
    { label: 'V', value: 5, name: 'Vineri' },
    { label: 'S', value: 6, name: 'Sâmbătă' },
    { label: 'D', value: 0, name: 'Duminică' }
  ];

  // Load initial data
  useEffect(() => {
    fetchInstitutions();
    fetchMedics();
  }, []);

  // Load shifts for table view
  useEffect(() => {
    if (activeTab === 'table') {
      setShifts([]);
      fetchShifts();
    }
  }, [activeTab, page, rowsPerPage, selectedInstitution, selectedDoctor, selectedModalities, selectedStatuses, dateRange, sortOrder]);

  // Load shifts for calendar view
  useEffect(() => {
    if (activeTab === 'calendar') {
      setExpandedShifts([]);
      fetchExpandedShiftsForCalendar();
    }
  }, [activeTab, calendarMonth, calendarYear, selectedInstitution, selectedDoctor, selectedModalities, selectedStatuses]);

  const fetchShifts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      // Always filter for doctor shifts only
      params.append('shiftType', 'doctor');

      // For medic role, filter only their shifts
      if (user?.role === 'medic') {
        params.append('doctor_id', user.id);
      }

      params.append('page', String(page + 1));
      params.append('size', String(rowsPerPage));
      params.append('sortBy', 'start_at');
      params.append('sortDirection', sortOrder);

      if (selectedInstitution !== 'all') {
        params.append('institutionIds', selectedInstitution);
      }

      if (selectedDoctor && user?.role !== 'medic') {
        params.append('doctorIds', selectedDoctor.id);
      }

      if (selectedModalities.length > 0) {
        selectedModalities.forEach(mod => params.append('modalities', mod));
      }

      if (dateRange.start) {
        const startDateWithTime = new Date(dateRange.start);
        startDateWithTime.setHours(0, 0, 0, 0);
        params.append('from', startDateWithTime.toISOString());
      }
      if (dateRange.end) {
        const endDateWithTime = new Date(dateRange.end);
        endDateWithTime.setHours(23, 59, 59, 999);
        params.append('to', endDateWithTime.toISOString());
      }

      const response = await apiClient.get(`/shifts/?${params.toString()}`);
      let shiftsData = response.data.shifts || [];

      // Apply status filter on frontend (computed field)
      if (selectedStatuses.length > 0) {
        shiftsData = shiftsData.filter((shift: Shift) =>
          selectedStatuses.includes(getShiftStatus(shift))
        );
        setTotalShifts(shiftsData.length);
      } else {
        setTotalShifts(response.data.total || 0);
      }

      setShifts(shiftsData);
    } catch (err) {
      console.error('Error fetching shifts:', err);
      setError('Eroare la încărcarea gărzilor');
    } finally {
      setLoading(false);
    }
  };

  const fetchExpandedShiftsForCalendar = async () => {
    try {
      setCalendarLoading(true);
      const calendarStart = new Date(calendarYear, calendarMonth, 1);
      calendarStart.setDate(calendarStart.getDate() - 7);

      const calendarEnd = new Date(calendarYear, calendarMonth + 1, 0);
      calendarEnd.setDate(calendarEnd.getDate() + 7);
      calendarEnd.setHours(23, 59, 59, 999);

      const params = new URLSearchParams();

      // Always filter for doctor shifts only
      params.append('shiftType', 'doctor');

      if (user?.role === 'medic') {
        params.append('doctor_id', user.id);
      }

      params.append('expand', 'true');
      params.append('start_date', calendarStart.toISOString().split('T')[0]);
      params.append('end_date', calendarEnd.toISOString().split('T')[0]);
      params.append('sortBy', 'start_at');
      params.append('sortDirection', 'asc');

      if (selectedInstitution !== 'all') {
        params.append('institutionIds', selectedInstitution);
      }

      if (user?.role !== 'medic' && selectedDoctor) {
        params.append('doctorIds', selectedDoctor.id);
      }

      if (selectedModalities.length > 0) {
        selectedModalities.forEach(mod => params.append('modalities', mod));
      }

      const response = await apiClient.get(`/shifts/?${params.toString()}`);
      let shiftsData = response.data.shifts || [];

      if (selectedStatuses.length > 0) {
        shiftsData = shiftsData.filter((shift: Shift) =>
          selectedStatuses.includes(getShiftStatus(shift))
        );
      }

      setExpandedShifts(shiftsData);
    } catch (err) {
      console.error('Error fetching expanded shifts for calendar:', err);
    } finally {
      setCalendarLoading(false);
    }
  };

  const fetchInstitutions = async () => {
    try {
      if (user?.role === 'medic') {
        const response = await apiClient.get(`/users/${user.id}/institutions`);
        setInstitutions(response.data || []);
      } else {
        const response = await apiClient.get('/tenants/institutions');
        setInstitutions(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching institutions:', err);
    }
  };

  const fetchMedics = async () => {
    try {
      const response = await apiClient.get('/users/?role=medic');
      setMedics(response.data || []);
    } catch (err) {
      console.error('Error fetching medics:', err);
    }
  };

  const handleOpenDialog = (shift?: Shift) => {
    if (shift) {
      setEditingShift(shift);
      setFormData({
        institution_id: shift.institution_id,
        doctor_id: shift.doctor_id,
        start_at: shift.start_at,
        end_at: shift.end_at,
        modalities: shift.modalities,
        auto_assign: shift.auto_assign,
        notes: shift.notes || ''
      });

      if (shift.is_recurring && shift.recurrence_pattern) {
        const pattern = shift.recurrence_pattern;
        setIsRecurring(true);
        setRecurringStartDate(new Date(pattern.start_date));
        setRecurringEndDate(new Date(pattern.end_date));
        setSelectedDays(pattern.days_of_week);

        const [startHour, startMinute] = pattern.time_start.split(':').map(Number);
        const [endHour, endMinute] = pattern.time_end.split(':').map(Number);

        const startTime = new Date();
        startTime.setHours(startHour, startMinute, 0, 0);
        setShiftStartTime(startTime);

        const endTime = new Date();
        endTime.setHours(endHour, endMinute, 0, 0);
        setShiftEndTime(endTime);

        setSingleStartDateTime(null);
        setSingleEndDateTime(null);
      } else {
        setIsRecurring(false);
        setSingleStartDateTime(new Date(shift.start_at));
        setSingleEndDateTime(new Date(shift.end_at));
        setRecurringStartDate(null);
        setRecurringEndDate(null);
        setSelectedDays([]);
        setShiftStartTime(null);
        setShiftEndTime(null);
      }
    } else {
      setEditingShift(null);
      setFormData({
        institution_id: '',
        doctor_id: '',
        start_at: '',
        end_at: '',
        modalities: [],
        auto_assign: true,
        notes: ''
      });
      setSingleStartDateTime(null);
      setSingleEndDateTime(null);
      setIsRecurring(false);
      setRecurringStartDate(null);
      setRecurringEndDate(null);
      setSelectedDays([]);
      setShiftStartTime(null);
      setShiftEndTime(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingShift(null);
    setError(null);
    setFormData({
      institution_id: '',
      doctor_id: '',
      start_at: '',
      end_at: '',
      modalities: [],
      auto_assign: true,
      notes: ''
    });
    setSingleStartDateTime(null);
    setSingleEndDateTime(null);
    setIsRecurring(false);
    setRecurringStartDate(null);
    setRecurringEndDate(null);
    setSelectedDays([]);
    setShiftStartTime(null);
    setShiftEndTime(null);
  };

  const handleDayToggle = (day: number) => {
    setSelectedDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day].sort()
    );
  };

  const generateRecurringShifts = () => {
    if (!recurringStartDate || !recurringEndDate || !shiftStartTime || !shiftEndTime || selectedDays.length === 0) {
      return [];
    }

    const shiftsArr: { start_at: string; end_at: string }[] = [];
    const currentDate = new Date(recurringStartDate);
    const endDate = new Date(recurringEndDate);

    while (currentDate <= endDate) {
      const dayOfWeek = currentDate.getDay();

      if (selectedDays.includes(dayOfWeek)) {
        const shiftStart = new Date(currentDate);
        shiftStart.setHours(shiftStartTime.getHours(), shiftStartTime.getMinutes(), 0, 0);

        const shiftEnd = new Date(currentDate);
        shiftEnd.setHours(shiftEndTime.getHours(), shiftEndTime.getMinutes(), 0, 0);

        if (shiftEnd <= shiftStart) {
          shiftEnd.setDate(shiftEnd.getDate() + 1);
        }

        shiftsArr.push({
          start_at: shiftStart.toISOString(),
          end_at: shiftEnd.toISOString()
        });
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return shiftsArr;
  };

  const handleSubmit = async () => {
    try {
      if (!formData.institution_id || !formData.doctor_id) {
        setError('Instituția și medicul sunt obligatorii');
        return;
      }

      if (formData.modalities.length === 0) {
        setError('Cel puțin o modalitate trebuie selectată');
        return;
      }

      if (editingShift) {
        if (isRecurring) {
          if (!recurringStartDate || !recurringEndDate || !shiftStartTime || !shiftEndTime) {
            setError('Toate câmpurile de interval și timp sunt obligatorii pentru gărzi recursive');
            return;
          }

          if (selectedDays.length === 0) {
            setError('Selectați cel puțin o zi a săptămânii');
            return;
          }

          const startDateStr = recurringStartDate.toISOString().split('T')[0];
          const endDateStr = recurringEndDate.toISOString().split('T')[0];
          const timeStartStr = `${shiftStartTime.getHours().toString().padStart(2, '0')}:${shiftStartTime.getMinutes().toString().padStart(2, '0')}`;
          const timeEndStr = `${shiftEndTime.getHours().toString().padStart(2, '0')}:${shiftEndTime.getMinutes().toString().padStart(2, '0')}`;

          const shiftData = {
            ...formData,
            start_at: new Date(recurringStartDate.getFullYear(), recurringStartDate.getMonth(), recurringStartDate.getDate(), shiftStartTime.getHours(), shiftStartTime.getMinutes()).toISOString(),
            end_at: new Date(recurringEndDate.getFullYear(), recurringEndDate.getMonth(), recurringEndDate.getDate(), shiftEndTime.getHours(), shiftEndTime.getMinutes()).toISOString(),
            is_recurring: true,
            recurrence_pattern: {
              start_date: startDateStr,
              end_date: endDateStr,
              days_of_week: selectedDays,
              time_start: timeStartStr,
              time_end: timeEndStr
            },
            shift_type: 'doctor',
          };

          await apiClient.put(`/shifts/${editingShift.id}`, shiftData);
          alert('Gardă recursivă actualizată cu succes!');
          handleCloseDialog();
          fetchShifts();
        } else {
          if (!singleStartDateTime || !singleEndDateTime) {
            setError('Data și ora de început și sfârșit sunt obligatorii');
            return;
          }

          const shiftData = {
            ...formData,
            start_at: singleStartDateTime.toISOString(),
            end_at: singleEndDateTime.toISOString(),
            is_recurring: false,
            recurrence_pattern: null,
            shift_type: 'doctor',
          };

          await apiClient.put(`/shifts/${editingShift.id}`, shiftData);
          handleCloseDialog();
          fetchShifts();
        }
      } else {
        if (isRecurring) {
          if (!recurringStartDate || !recurringEndDate || !shiftStartTime || !shiftEndTime) {
            setError('Toate câmpurile de interval și timp sunt obligatorii pentru gărzi recursive');
            return;
          }

          if (selectedDays.length === 0) {
            setError('Selectați cel puțin o zi a săptămânii');
            return;
          }

          const startDateStr = recurringStartDate.toISOString().split('T')[0];
          const endDateStr = recurringEndDate.toISOString().split('T')[0];
          const timeStartStr = `${shiftStartTime.getHours().toString().padStart(2, '0')}:${shiftStartTime.getMinutes().toString().padStart(2, '0')}`;
          const timeEndStr = `${shiftEndTime.getHours().toString().padStart(2, '0')}:${shiftEndTime.getMinutes().toString().padStart(2, '0')}`;

          const shiftData = {
            ...formData,
            start_at: new Date(recurringStartDate.getFullYear(), recurringStartDate.getMonth(), recurringStartDate.getDate(), shiftStartTime.getHours(), shiftStartTime.getMinutes()).toISOString(),
            end_at: new Date(recurringEndDate.getFullYear(), recurringEndDate.getMonth(), recurringEndDate.getDate(), shiftEndTime.getHours(), shiftEndTime.getMinutes()).toISOString(),
            is_recurring: true,
            recurrence_pattern: {
              start_date: startDateStr,
              end_date: endDateStr,
              days_of_week: selectedDays,
              time_start: timeStartStr,
              time_end: timeEndStr
            },
            shift_type: 'doctor',
          };

          await apiClient.post('/shifts/', shiftData);
          alert(`Gardă recursivă creată cu succes! Se va repeta pe ${daysOfWeek.filter(d => selectedDays.includes(d.value)).map(d => d.name).join(', ')} între ${startDateStr} și ${endDateStr}.`);
          handleCloseDialog();
          fetchShifts();
        } else {
          if (!singleStartDateTime || !singleEndDateTime) {
            setError('Data și ora de început și sfârșit sunt obligatorii');
            return;
          }

          const shiftData = {
            ...formData,
            start_at: singleStartDateTime.toISOString(),
            end_at: singleEndDateTime.toISOString(),
            shift_type: 'doctor',
          };

          await apiClient.post('/shifts/', shiftData);
          handleCloseDialog();
          fetchShifts();
        }
      }
    } catch (err) {
      console.error('Error saving shift:', err);
      setError('Eroare la salvarea gărzii');
    }
  };

  const handleDelete = async (shiftId: string) => {
    if (window.confirm('Sigur doriți să ștergeți această gardă?')) {
      try {
        await apiClient.delete(`/shifts/${shiftId}`);
        fetchShifts();
      } catch (err) {
        console.error('Error deleting shift:', err);
        setError('Eroare la ștergerea gărzii');
      }
    }
  };

  const handleModalitiesChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      modalities: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const getInstitutionName = (shift: Shift) => {
    if (shift.institution_name) return shift.institution_name;
    const institution = institutions.find(inst => inst.id === shift.institution_id);
    return institution?.name || `Instituție ${shift.institution_id.substring(0, 8)}...`;
  };

  const getMedicName = (shift: Shift) => {
    if (shift.doctor_name) return shift.doctor_name;
    const medic = medics.find(m => m.id === shift.doctor_id);
    if (medic?.doctor_profile) {
      return `${medic.doctor_profile.first_name} ${medic.doctor_profile.last_name}`;
    }
    return medic?.email || `Doctor ${shift.doctor_id.substring(0, 8)}...`;
  };

  // Color palette for badge borders
  const BADGE_BORDER_COLORS = [
    '#4299e1', '#9b59b6', '#f59e0b', '#ec4899', '#14b8a6',
    '#eab308', '#10b981', '#f97316', '#8b5cf6', '#06b6d4', '#84cc16', '#f43f5e',
  ];

  const stringToColorIndex = (str: string): number => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash;
    }
    return Math.abs(hash) % BADGE_BORDER_COLORS.length;
  };

  const getMedicBorderColor = (doctorId: string): string => {
    return BADGE_BORDER_COLORS[stringToColorIndex(doctorId)];
  };

  const getInstitutionBorderColor = (institutionId: string): string => {
    return BADGE_BORDER_COLORS[stringToColorIndex(institutionId + '_inst')];
  };

  const getStatusDisplay = (
    positionInShift: 'first' | 'middle' | 'last' | 'single',
    shift: Shift
  ): { emoji: string; text: string; color: string } => {
    const startTime = new Date(shift.start_at).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
    const endTime = new Date(shift.end_at).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });

    switch (positionInShift) {
      case 'first':
        return { emoji: '🟢', text: `ÎNCEPE: ${startTime}`, color: '#22c55e' };
      case 'middle':
        return { emoji: '🔵', text: 'CONTINUĂ', color: '#9e9e9e' };
      case 'last':
        return { emoji: '🔴', text: `SFÂRȘIT: ${endTime}`, color: '#4299e1' };
      case 'single':
        return { emoji: '🟢', text: `${startTime} - ${endTime}`, color: '#22c55e' };
      default:
        return { emoji: '🟢', text: `${startTime} - ${endTime}`, color: '#22c55e' };
    }
  };

  const getShiftStatus = (shift: Shift) => {
    const now = new Date();
    const shiftStart = new Date(shift.start_at);
    const shiftEnd = new Date(shift.end_at);

    if (shift.status === 'finalizata') return 'finalizata';
    if (now < shiftStart) return 'planificata';
    if (now >= shiftStart && now <= shiftEnd) return 'in_curs';
    return 'finalizata';
  };

  const handleResetFilters = useCallback(() => {
    setSelectedInstitution('all');
    setSelectedDoctor(null);
    setDoctorInputValue('');
    setSelectedModalities([]);
    setSelectedStatuses([]);
    setDatePreset('current_month');
    const range = getMonthRange(0);
    setDateRange(range);
    setCalendarMonth(range.start ? range.start.getMonth() : new Date().getMonth());
    setCalendarYear(range.start ? range.start.getFullYear() : new Date().getFullYear());
    setPage(0);
  }, []);

  const handleToggleSortOrder = useCallback(() => {
    setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
  }, []);

  const handleInstitutionChange = useCallback((institutionId: string) => {
    setSelectedInstitution(institutionId);
    setPage(0);
  }, []);

  const handleDoctorInputChange = useCallback((value: string, reason: string) => {
    setDoctorInputValue(value);
    if (reason === 'clear') {
      setSelectedDoctor(null);
      setPage(0);
    }
  }, []);

  const handleDoctorChange = useCallback((value: DoctorOption | null) => {
    setSelectedDoctor(value as User | null);
    const label = value
      ? `${value.doctor_profile?.first_name || ''} ${value.doctor_profile?.last_name || ''}`.trim() || value.email || ''
      : '';
    setDoctorInputValue(label);
    setPage(0);
  }, []);

  const handleStatusToggle = useCallback((status: string) => {
    setSelectedStatuses(prev => {
      const next = prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status];
      setPage(0);
      return next;
    });
  }, []);

  const handleModalityToggle = useCallback((modality: string) => {
    setSelectedModalities(prev => {
      const next = prev.includes(modality) ? prev.filter(m => m !== modality) : [...prev, modality];
      setPage(0);
      return next;
    });
  }, []);

  const handleDateRangeChange = useCallback((range: DateRangeValue) => {
    setDateRange(range);
    setDatePreset(null);
    if (range.start) {
      setCalendarMonth(range.start.getMonth());
      setCalendarYear(range.start.getFullYear());
    }
    setPage(0);
  }, []);

  const handleDatePresetChange = useCallback((preset: DatePreset | null) => {
    setDatePreset(preset);
    setPage(0);
  }, []);

  const handleCalendarMonthChange = useCallback((month: number) => {
    setCalendarMonth(month);
  }, []);

  const handleCalendarYearChange = useCallback((year: number) => {
    setCalendarYear(year);
  }, []);

  const handleViewChange = useCallback((view: 'calendar' | 'table') => {
    setActiveTab(view);
  }, []);

  const statusOptions: StatusOption[] = useMemo(() => [
    { value: 'planificata', label: 'Programată' },
    { value: 'in_curs', label: 'În desfășurare' },
    { value: 'finalizata', label: 'Finalizată' }
  ], []);

  const canAddShift = !!(user?.role && ['admin', 'operator'].includes(user.role));

  return (
    <Box>
      <PageHeader
        title="Gărzi Medici"
        actions={null}
      />

      <Box sx={{ px: 3, py: 0.6 }}>
        {/* Filters */}
        <ShiftsFiltersBar
          selectedInstitution={selectedInstitution}
          availableInstitutions={institutions}
          onInstitutionChange={handleInstitutionChange}
          showDoctorFilter={user?.role !== 'medic'}
          doctorOptions={medics as DoctorOption[]}
          doctorValue={selectedDoctor as DoctorOption | null}
          doctorInputValue={doctorInputValue}
          loadingDoctorOptions={false}
          onDoctorInputChange={handleDoctorInputChange}
          onDoctorChange={handleDoctorChange}
          selectedStatuses={selectedStatuses}
          statusOptions={statusOptions}
          onStatusToggle={handleStatusToggle}
          selectedModalities={selectedModalities}
          availableModalities={availableModalities}
          onModalityToggle={handleModalityToggle}
          dateRange={dateRange}
          datePreset={datePreset}
          onDateRangeChange={handleDateRangeChange}
          onDatePresetChange={handleDatePresetChange}
          calendarMonth={calendarMonth}
          calendarYear={calendarYear}
          onCalendarMonthChange={handleCalendarMonthChange}
          onCalendarYearChange={handleCalendarYearChange}
          onClearFilters={handleResetFilters}
          activeView={activeTab}
          onViewChange={handleViewChange}
          canAddShift={canAddShift}
          onAddShift={() => handleOpenDialog()}
          addButtonLabel="Adaugă Gardă"
          shiftType="doctor"
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Table View Loading */}
        {activeTab === 'table' && loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <Typography color="text.secondary">Se încarcă gărzile...</Typography>
          </Box>
        )}

        {/* Calendar View Loading */}
        {activeTab === 'calendar' && calendarLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <Typography color="text.secondary">Se încarcă gărzile...</Typography>
          </Box>
        )}

        {/* Table View Empty */}
        {activeTab === 'table' && !loading && shifts.length === 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" textAlign="center" color="text.secondary">
                Nu există gărzi programate
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Table View */}
        {activeTab === 'table' && !loading && shifts.length > 0 && (
          <Paper sx={{ width: '100%', overflow: 'hidden', mt: 0 }}>
            <TableContainer>
              <Table
                stickyHeader
                size="small"
                sx={{
                  '& .MuiTableCell-root': { py: 0.2 },
                  '& .MuiTypography-root': { fontSize: '0.84rem', lineHeight: 1.15 },
                  '& .MuiChip-root': { height: 17, fontSize: '0.78rem' },
                  '& .MuiTableBody-root .MuiIconButton-root': { p: '2px' },
                  '& .MuiTableBody-root .MuiIconButton-root .MuiSvgIcon-root': { fontSize: 15 },
                }}
              >
                <TableHead>
                  <TableRow sx={{ '& th': { padding: '6px 12px', height: '34px', fontSize: '0.8rem', fontWeight: 700, lineHeight: 1.2, whiteSpace: 'nowrap' } }}>
                    <TableCell>Instituție</TableCell>
                    <TableCell>Medic</TableCell>
                    <TableCell onClick={handleToggleSortOrder} sx={{ cursor: 'pointer', userSelect: 'none', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}>
                      Perioada {sortOrder === 'desc' ? '↓' : '↑'}
                    </TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Modalități</TableCell>
                    <TableCell>Auto</TableCell>
                    <TableCell>Note</TableCell>
                    <TableCell>Acțiuni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shifts.map((shift) => (
                    <TableRow key={shift.id} hover>
                      <TableCell>{getInstitutionName(shift)}</TableCell>
                      <TableCell>{getMedicName(shift)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDateTime(shift.start_at)} → {formatDateTime(shift.end_at)}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const currentStatus = getShiftStatus(shift);
                          const statusLabel = currentStatus === 'planificata' ? 'Programată' :
                            currentStatus === 'in_curs' ? 'În curs' : 'Finalizată';
                          const borderColor = currentStatus === 'planificata' ? '#1976d2' :
                            currentStatus === 'in_curs' ? '#ff9800' : '#4caf50';
                          return (
                            <Chip
                              label={statusLabel}
                              size="small"
                              variant="outlined"
                              sx={{ borderColor, borderWidth: '1.5px', color: 'text.primary', backgroundColor: 'transparent', minWidth: '75px', justifyContent: 'center' }}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.25} flexWrap="wrap">
                          {shift.modalities.map((modality) => (
                            <Chip key={modality} label={modality} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={shift.auto_assign ? 'Da' : 'Nu'} color={shift.auto_assign ? 'success' : 'default'} size="small" />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 150 }}>
                        <Typography noWrap>{shift.notes || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5}>
                          {(() => {
                            const now = new Date();
                            const shiftStart = new Date(shift.start_at);
                            const isShiftFuture = shiftStart > now;
                            const canEdit = user?.role !== 'medic' && isShiftFuture;

                            if (canEdit) {
                              return (
                                <>
                                  <Tooltip title="Editează">
                                    <IconButton size="small" onClick={() => handleOpenDialog(shift)}>
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Șterge">
                                    <IconButton size="small" color="error" onClick={() => handleDelete(shift.id)}>
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              );
                            } else if (!isShiftFuture) {
                              return <Typography variant="body2" color="text.secondary">Nu se poate modifica</Typography>;
                            } else {
                              return <Typography variant="body2" color="text.secondary">Doar vizualizare</Typography>;
                            }
                          })()}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalShifts}
                page={page}
                onPageChange={(_event, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50, 100]}
                labelRowsPerPage="Gărzi per pagină:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} din ${count !== -1 ? count : `mai mult de ${to}`}`
                }
              />
            </TableContainer>
          </Paper>
        )}

        {/* Calendar View */}
        {activeTab === 'calendar' && !calendarLoading && (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: { xs: 0.5, sm: 1 },
                  width: '100%'
                }}>
                  {/* Days of week headers */}
                  {['Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă', 'Duminică'].map((day) => (
                    <Box key={day} sx={{
                      p: { xs: 0.25, sm: 0.5 },
                      textAlign: 'center',
                      fontWeight: 600,
                      bgcolor: 'primary.main',
                      border: '1px solid',
                      borderColor: 'primary.dark',
                      color: 'primary.contrastText',
                      fontSize: '0.75rem'
                    }}>
                      {day}
                    </Box>
                  ))}

                  {/* Calendar days */}
                  {(() => {
                    const firstDayOfMonth = new Date(calendarYear, calendarMonth, 1);
                    const lastDayOfMonth = new Date(calendarYear, calendarMonth + 1, 0);
                    const daysInMonth = lastDayOfMonth.getDate();
                    let firstDayOfWeek = firstDayOfMonth.getDay();
                    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
                    const totalCells = Math.ceil((firstDayOfWeek + daysInMonth) / 7) * 7;

                    return Array.from({ length: totalCells }, (_, i) => {
                      const dayNumber = i - firstDayOfWeek + 1;
                      const isCurrentMonth = dayNumber >= 1 && dayNumber <= daysInMonth;

                      let date: Date;
                      let displayDay: number;
                      let showShifts = isCurrentMonth;

                      if (dayNumber < 1) {
                        const prevMonth = new Date(calendarYear, calendarMonth, 0);
                        const prevMonthDays = prevMonth.getDate();
                        displayDay = prevMonthDays + dayNumber;
                        date = new Date(calendarYear, calendarMonth - 1, displayDay);
                        showShifts = (displayDay === prevMonthDays);
                      } else if (dayNumber > daysInMonth) {
                        displayDay = dayNumber - daysInMonth;
                        date = new Date(calendarYear, calendarMonth + 1, displayDay);
                        showShifts = (displayDay === 1);
                      } else {
                        displayDay = dayNumber;
                        date = new Date(calendarYear, calendarMonth, dayNumber);
                      }

                      const dayShifts = showShifts ? expandedShifts.filter(shift => {
                        const shiftStart = new Date(shift.start_at);
                        const shiftEnd = new Date(shift.end_at);
                        const currentDate = new Date(date);
                        shiftStart.setHours(0, 0, 0, 0);
                        shiftEnd.setHours(0, 0, 0, 0);
                        currentDate.setHours(0, 0, 0, 0);
                        return currentDate >= shiftStart && currentDate <= shiftEnd;
                      }) : [];

                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const dateCompare = new Date(date);
                      dateCompare.setHours(0, 0, 0, 0);
                      const isToday = dateCompare.getTime() === today.getTime();

                      return (
                        <Box
                          key={i}
                          sx={{
                            p: { xs: 0.5, sm: 0.75, md: 1 },
                            minHeight: { xs: '60px', sm: '80px', md: '100px', lg: '120px' },
                            height: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            border: isToday ? '2px solid' : '1px solid',
                            borderColor: isToday ? 'primary.main' : 'divider',
                            bgcolor: isCurrentMonth ? 'background.default' : 'action.disabledBackground',
                            opacity: isCurrentMonth ? 1 : 0.6,
                            position: 'relative',
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          <Typography variant="body2" sx={{
                            position: 'absolute',
                            top: { xs: 1, sm: 2 },
                            left: { xs: 2, sm: 4 },
                            color: isCurrentMonth ? 'text.secondary' : 'text.disabled',
                            fontWeight: 'medium',
                            fontSize: { xs: '0.7rem', sm: '0.875rem' }
                          }}>
                            {displayDay}
                          </Typography>

                          {dayShifts.length === 0 ? (
                            <Box sx={{
                              mt: { xs: 2.5, sm: 3 },
                              p: { xs: 0.5, sm: 1 },
                              textAlign: 'center',
                              color: 'text.disabled',
                              fontSize: { xs: '0.6rem', sm: '0.7rem' },
                              flex: 1
                            }}>
                              {isCurrentMonth ? 'Fără gărzi' : ''}
                            </Box>
                          ) : (
                            <Box sx={{
                              mt: { xs: 2.5, sm: 3 },
                              display: 'flex',
                              flexDirection: 'column',
                              gap: { xs: 0.5, sm: 0.75 },
                              flex: 1
                            }}>
                              {dayShifts.map((shift) => {
                                const shiftStart = new Date(shift.start_at);
                                const shiftEnd = new Date(shift.end_at);
                                const currentDate = new Date(date);
                                currentDate.setHours(0, 0, 0, 0);
                                const startDate = new Date(shiftStart);
                                startDate.setHours(0, 0, 0, 0);
                                const endDate = new Date(shiftEnd);
                                endDate.setHours(0, 0, 0, 0);

                                const isFirstDay = currentDate.getTime() === startDate.getTime();
                                const isLastDay = currentDate.getTime() === endDate.getTime();
                                const isMultiDay = startDate.getTime() !== endDate.getTime();

                                let positionInShift: 'first' | 'middle' | 'last' | 'single';
                                if (!isMultiDay) positionInShift = 'single';
                                else if (isFirstDay) positionInShift = 'first';
                                else if (isLastDay) positionInShift = 'last';
                                else positionInShift = 'middle';

                                const medicBorderColor = getMedicBorderColor(shift.doctor_id);
                                const institutionBorderColor = getInstitutionBorderColor(shift.institution_id);
                                const statusDisplay = getStatusDisplay(positionInShift, shift);

                                return (
                                  <Box
                                    key={shift.id}
                                    sx={{
                                      p: { xs: 0.75, sm: 1 },
                                      bgcolor: 'background.paper',
                                      borderRadius: 2,
                                      cursor: 'default',
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      boxShadow: 1,
                                      userSelect: 'none',
                                      overflow: 'hidden',
                                      flex: '0 0 auto',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 0.75
                                    }}
                                  >
                                    <Box sx={{ borderLeft: `3px solid ${medicBorderColor}`, paddingLeft: 1, paddingY: 0.5 }}>
                                      <Typography variant="caption" sx={{ color: 'text.primary', fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {getMedicName(shift)}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ borderLeft: `3px solid ${institutionBorderColor}`, paddingLeft: 1, paddingY: 0.5 }}>
                                      <Typography variant="caption" sx={{ color: 'text.primary', fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 500, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {getInstitutionName(shift)}
                                      </Typography>
                                    </Box>
                                    <Typography variant="caption" sx={{ color: 'text.primary', fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      <Box component="span" sx={{ color: statusDisplay.color, fontWeight: 700, mr: 0.5 }}>
                                        {statusDisplay.emoji}
                                      </Box>
                                      {statusDisplay.text}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.6rem', sm: '0.65rem' }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {shift.modalities.join(' • ')}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          )}
                        </Box>
                      );
                    });
                  })()}
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>

      {/* Dialog for create/edit shift */}
      <Dialog
        open={openDialog}
        onClose={(_, reason) => { if (reason !== 'backdropClick') handleCloseDialog(); }}
        disableEscapeKeyDown
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            '& .MuiInputLabel-root': { color: 'text.secondary', fontSize: '0.85rem' },
            '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
            '& .MuiOutlinedInput-root': { fontSize: '0.875rem' },
          }
        }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}>
          {editingShift ? 'Editează Gardă' : 'Adaugă Gardă Nouă'}
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Instituție</InputLabel>
                  <Select value={formData.institution_id} onChange={(e) => setFormData(prev => ({ ...prev, institution_id: e.target.value }))} label="Instituție">
                    {institutions.map((institution) => (
                      <MenuItem key={institution.id} value={institution.id}>{institution.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Medic</InputLabel>
                  <Select value={formData.doctor_id} onChange={(e) => setFormData(prev => ({ ...prev, doctor_id: e.target.value }))} label="Medic">
                    {medics.map((medic) => (
                      <MenuItem key={medic.id} value={medic.id}>
                        {medic.doctor_profile ? `${medic.doctor_profile.first_name} ${medic.doctor_profile.last_name}` : medic.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {!editingShift && (
                <>
                  <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.85rem', color: 'text.primary' } }}
                      control={<Switch checked={isRecurring} onChange={(e) => setIsRecurring(e.target.checked)} color="primary" size="small" />}
                      label={
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <RepeatIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
                          <Typography variant="body2" sx={{ color: 'text.primary' }}>Programare recursivă</Typography>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>
                </>
              )}

              {!isRecurring ? (
                <>
                  <Grid item xs={12} md={6}>
                    <DateTimePicker label="Data și ora început" value={singleStartDateTime} onChange={(newValue) => setSingleStartDateTime(newValue)} ampm={false} slotProps={{ textField: { fullWidth: true, required: true, size: 'small' } }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DateTimePicker label="Data și ora sfârșit" value={singleEndDateTime} onChange={(newValue) => setSingleEndDateTime(newValue)} ampm={false} slotProps={{ textField: { fullWidth: true, required: true, size: 'small' } }} />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12}><Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Interval de date</Typography></Grid>
                  <Grid item xs={12} md={6}>
                    <DatePicker label="Data început" value={recurringStartDate} onChange={(newValue) => setRecurringStartDate(newValue)} slotProps={{ textField: { fullWidth: true, required: true, size: 'small' } }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DatePicker label="Data sfârșit" value={recurringEndDate} onChange={(newValue) => setRecurringEndDate(newValue)} slotProps={{ textField: { fullWidth: true, required: true, size: 'small' } }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 0.5 }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', textTransform: 'uppercase', letterSpacing: '0.5px', mt: 1, display: 'block' }}>Interval orar</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TimePicker label="Ora început" value={shiftStartTime} onChange={(newValue) => setShiftStartTime(newValue)} ampm={false} slotProps={{ textField: { fullWidth: true, required: true, size: 'small' } }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TimePicker label="Ora sfârșit" value={shiftEndTime} onChange={(newValue) => setShiftEndTime(newValue)} ampm={false} slotProps={{ textField: { fullWidth: true, required: true, size: 'small' } }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 0.5 }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', textTransform: 'uppercase', letterSpacing: '0.5px', mt: 1, display: 'block', mb: 1 }}>Zile săptămână</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {daysOfWeek.map((day) => (
                        <Chip
                          key={day.value}
                          label={day.label}
                          onClick={() => handleDayToggle(day.value)}
                          size="small"
                          sx={{
                            fontSize: '0.75rem', height: '26px', fontWeight: selectedDays.includes(day.value) ? 600 : 400,
                            bgcolor: selectedDays.includes(day.value) ? 'primary.main' : 'transparent',
                            color: selectedDays.includes(day.value) ? 'primary.contrastText' : 'text.primary',
                            border: '1px solid', borderColor: selectedDays.includes(day.value) ? 'primary.main' : 'divider',
                            '&:hover': { bgcolor: selectedDays.includes(day.value) ? 'primary.dark' : 'action.hover' }
                          }}
                        />
                      ))}
                    </Box>
                    {selectedDays.length > 0 && (
                      <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary', fontSize: '0.7rem' }}>
                        Selectate: {selectedDays.map(d => daysOfWeek.find(day => day.value === d)?.name).join(', ')}
                      </Typography>
                    )}
                  </Grid>
                  {recurringStartDate && recurringEndDate && selectedDays.length > 0 && shiftStartTime && shiftEndTime && (
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
                        Se vor crea <strong>{generateRecurringShifts().length} gărzi</strong>
                      </Alert>
                    </Grid>
                  )}
                </>
              )}

              <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Modalități</InputLabel>
                  <Select
                    multiple
                    value={formData.modalities}
                    onChange={handleModalitiesChange}
                    input={<OutlinedInput label="Modalități" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" sx={{ height: '20px', fontSize: '0.7rem', bgcolor: 'primary.main', color: 'primary.contrastText' }} />
                        ))}
                      </Box>
                    )}
                  >
                    {availableModalities.map((modality) => (
                      <MenuItem key={modality} value={modality} sx={{ fontSize: '0.85rem', py: 0.5 }}>{modality}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.85rem', color: 'text.primary' } }}
                  control={<Switch checked={formData.auto_assign} onChange={(e) => setFormData(prev => ({ ...prev, auto_assign: e.target.checked }))} color="primary" size="small" />}
                  label="Auto-assign studii"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="Note" multiline rows={2} value={formData.notes} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))} />
              </Grid>
            </Grid>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
          <Button onClick={handleCloseDialog} size="small" sx={{ fontSize: '0.8rem', color: 'text.secondary', '&:hover': { color: 'text.primary' } }}>
            Anulează
          </Button>
          <Button onClick={handleSubmit} variant="contained" size="small" sx={{ fontSize: '0.8rem' }}>
            {editingShift ? 'Salvează' : 'Adaugă'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error">{error}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ShiftsPage;
